import { useQueryClient } from '@tanstack/vue-query';
import { useFreeTierRemainingQuery, useFreeTierOnboardingQuery, type FreeTierRemaining } from '~/api/queries/freeTier';
import { queries } from '~/keys';
import { useFreePlan } from '~/composables/useFreePlan';

// Shared free-tier state for Version-B leads: are they a free-tier lead, and how many
// free uses remain per feature. Backed by /api/free-tier/remaining (only fetched when
// free-tier, so normal agents never hit it). Single source of truth for the inline
// isFreeTier checks B1 scattered across /ask and the marketing wizard.
export const useFreeTier = () => {
  const authStore = useAuthStore();
  const { isB } = useExperiment();
  const { isFreePlanUser } = useFreePlan();
  const { isUae } = useUaeGate();
  const queryClient = useQueryClient();

  const isFreeTier = computed(
    () => isB.value && !(authStore.user as { masterclassPaidAt?: string | null } | null)?.masterclassPaidAt,
  );

  const dataEnabled = computed(() => isFreeTier.value || isFreePlanUser.value || isUae.value);

  const query = useFreeTierRemainingQuery(dataEnabled);

  const onboardingQuery = useFreeTierOnboardingQuery(dataEnabled);
  const onboarding = computed(() => onboardingQuery.data.value ?? null);
  const onboardingComplete = computed(() => {
    const o = onboardingQuery.data.value;
    return !!o && o.lesson && o.chat && o.marketing && o.deal;
  });

  const chatRemaining = computed(() => query.data.value?.chat.remaining ?? null);
  const marketingRemaining = computed(() => query.data.value?.marketing.remaining ?? null);

  // Optimistically record one consumed free use so the counter ticks down immediately
  // and correctly. We do NOT refetch here: the server's usage row is eventually
  // consistent (the /ask AiAuditLog success row is written in onFinish, after the
  // stream ends), so an immediate refetch would read the pre-request count and show
  // one too many. Each successful use consumes exactly one credit, so the decrement is
  // exact; the next mount refetch reconciles against the server.
  const consumeFree = (feature: 'chat' | 'marketing') => {
    queryClient.setQueryData<FreeTierRemaining>(queries.freeTier.remaining.queryKey, (old) => {
      if (!old) return old;
      const f = old[feature];
      return { ...old, [feature]: { ...f, used: f.used + 1, remaining: Math.max(0, f.remaining - 1) } };
    });
  };

  return {
    isFreeTier,
    chatRemaining,
    marketingRemaining,
    consumeFree,
    refetchRemaining: query.refetch,
    onboarding,
    onboardingComplete,
  };
};
